/**
 * Shipments tab for the Order Detail page.
 * Renders one card per shipment, or a single pending card from the order destination
 * before the order ships. Falls back to an empty state when there is no destination.
 */
import React from "react";
import { Box, createStyles, Skeleton } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { useOutletContext } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { featureFlags } from "../../../featureFlags";
import { Body } from "../../../components/Typography";
import { ShipmentCard } from "./shipments/ShipmentCard";
import { toShipmentCardViews } from "./shipments/shipmentHelpers";
import { OrderViewOutletContext } from "./order-details/shared";
import { QueryErrorBox } from "../../../components/QueryErrorBox";

const useStyles = createStyles({
  container: {
    padding: tokens.spacing[4],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
  },
  emptyState: {
    padding: tokens.spacing[4],
  },
});

export const OrderShipmentsTab: React.FC = () => {
  const { classes } = useStyles();
  const { customerPublicId, customerOrderId } = useOutletContext<OrderViewOutletContext>();
  const user = useAuthUser();
  const { data, isLoading, error } = GraphQL.useOrderShipmentsQuery({
    orderIdentifier: {
      customerPublicId,
      customerOrderId,
    },
    isSandbox: user.isSandbox,
    withAssetServiceValidation: featureFlags.assetServiceValidation,
  });

  if (isLoading) {
    return (
      <Box data-testid="order-shipments-loading" className={classes.container}>
        <Skeleton height={220} radius="lg" />
        <Skeleton height={220} radius="lg" />
      </Box>
    );
  }

  if (error) {
    return <QueryErrorBox testId="order-shipments-error" message="Failed to load shipments." error={error} />;
  }

  const order = data?.orderByIdentifier;
  if (!order) return null;

  const cardViews = toShipmentCardViews(order);
  const currency = order.currency ?? GraphQL.SupportedCurrency.Usd;

  if (cardViews.length === 0) {
    return (
      <Box data-testid="order-shipments-loaded" className={classes.emptyState}>
        <Body>No shipments found for this order.</Body>
      </Box>
    );
  }

  return (
    <Box data-testid="order-shipments-loaded" className={classes.container}>
      {cardViews.map((view) => (
        <ShipmentCard key={view.key} view={view} currency={currency} isSuperAdmin={user.is.SUPER_ADMIN} />
      ))}
    </Box>
  );
};
